import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAdminMe, get2FASetup, enable2FA, disable2FA } from '../../api/auth';

export default function Admin2FAPage() {
  const queryClient = useQueryClient();
  const { data: admin, isLoading } = useQuery({
    queryKey: ['admin-me'],
    queryFn: getAdminMe,
  });

  const [mode, setMode] = useState<'idle' | 'setup' | 'disable'>('idle');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const enabled = admin?.totpEnabled ?? false;

  const startSetup = async () => {
    setError(''); setSuccess(''); setCode('');
    setIsBusy(true);
    try {
      const data = await get2FASetup();
      setQrDataUrl(data.qrCodeDataUrl);
      setSecret(data.secret);
      setMode('setup');
    } catch {
      setError('Errore durante la generazione del QR code.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsBusy(true);
    try {
      await enable2FA(code.trim());
      setSuccess('2FA attivato con successo!');
      setMode('idle');
      setCode('');
      queryClient.invalidateQueries({ queryKey: ['admin-me'] });
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Codice non valido.');
      setCode('');
    } finally {
      setIsBusy(false);
    }
  };

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsBusy(true);
    try {
      await disable2FA(code.trim());
      setSuccess('2FA disattivato.');
      setMode('idle');
      setCode('');
      queryClient.invalidateQueries({ queryKey: ['admin-me'] });
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Codice non valido.');
      setCode('');
    } finally {
      setIsBusy(false);
    }
  };

  if (isLoading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <h1 className="page-title">Autenticazione a due fattori</h1>
      </div>

      {success && (
        <div className="alert alert-success" style={{ marginBottom: '1.5rem' }}>{success}</div>
      )}

      {/* Status card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
            background: enabled ? 'var(--success-light)' : 'var(--gray-100)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.5rem',
          }}>
            {enabled ? '🔐' : '🔓'}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '1rem' }}>
              {enabled ? '2FA attivo' : '2FA non attivo'}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginTop: '0.2rem' }}>
              {enabled
                ? 'Il tuo account è protetto da un codice TOTP.'
                : 'Aggiungi un livello extra di sicurezza al login.'}
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <span className={`badge ${enabled ? 'badge-green' : 'badge-gray'}`}>
              {enabled ? 'Attivo' : 'Inattivo'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Setup flow ── */}
      {!enabled && mode === 'idle' && (
        <div className="card">
          <h3 style={{ marginBottom: '0.75rem' }}>Attiva il 2FA</h3>
          <p style={{ color: 'var(--gray-600)', marginBottom: '1.5rem', fontSize: '0.9375rem', lineHeight: 1.6 }}>
            Scansiona il QR code con Google Authenticator, Authy o un'altra app TOTP.
            Al prossimo login ti verrà chiesto il codice a 6 cifre.
          </p>
          <button className="btn btn-primary" onClick={startSetup} disabled={isBusy}>
            {isBusy ? 'Generazione QR code...' : 'Configura 2FA'}
          </button>
        </div>
      )}

      {!enabled && mode === 'setup' && (
        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>Scansiona il QR code</h3>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
            <img src={qrDataUrl} alt="QR Code 2FA" style={{ width: 200, height: 200, border: '1px solid var(--gray-200)', borderRadius: 8 }} />
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)', marginBottom: '0.5rem' }}>
            Non riesci a scansionare? Inserisci manualmente questa chiave nella tua app:
          </p>
          <code style={{
            display: 'block', background: 'var(--gray-50)', border: '1px solid var(--gray-200)',
            borderRadius: 6, padding: '0.625rem 0.875rem', fontSize: '0.875rem',
            letterSpacing: '0.1em', wordBreak: 'break-all', marginBottom: '1.5rem',
          }}>
            {secret}
          </code>

          {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}

          <form onSubmit={handleEnable}>
            <div className="form-group">
              <label className="form-label">Codice di verifica <span className="required">*</span></label>
              <input
                type="text" inputMode="numeric" maxLength={6}
                className="form-control"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                style={{ letterSpacing: '0.3em', fontSize: '1.25rem', textAlign: 'center', maxWidth: 200 }}
                autoFocus required
              />
              <span className="form-hint">Inserisci il codice mostrato nella tua app per confermare la configurazione.</span>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="submit" className="btn btn-primary" disabled={isBusy || code.length !== 6}>
                {isBusy ? 'Attivazione...' : 'Attiva 2FA'}
              </button>
              <button type="button" className="btn btn-ghost"
                onClick={() => { setMode('idle'); setCode(''); setError(''); }}>
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Disable flow ── */}
      {enabled && mode === 'idle' && (
        <div className="card">
          <h3 style={{ marginBottom: '0.75rem' }}>Disattiva il 2FA</h3>
          <p style={{ color: 'var(--gray-600)', marginBottom: '1.5rem', fontSize: '0.9375rem', lineHeight: 1.6 }}>
            Per disattivare la verifica in due passaggi devi confermare con il codice attuale dalla tua app.
          </p>
          <button className="btn btn-ghost" style={{ color: 'var(--danger)' }}
            onClick={() => { setMode('disable'); setError(''); setCode(''); }}>
            Disattiva 2FA
          </button>
        </div>
      )}

      {enabled && mode === 'disable' && (
        <div className="card">
          <h3 style={{ marginBottom: '1rem' }}>Conferma disattivazione</h3>
          {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}
          <form onSubmit={handleDisable}>
            <div className="form-group">
              <label className="form-label">Codice dalla tua app <span className="required">*</span></label>
              <input
                type="text" inputMode="numeric" maxLength={6}
                className="form-control"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                style={{ letterSpacing: '0.3em', fontSize: '1.25rem', textAlign: 'center', maxWidth: 200 }}
                autoFocus required
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="submit" className="btn btn-danger" disabled={isBusy || code.length !== 6}>
                {isBusy ? 'Disattivazione...' : 'Disattiva'}
              </button>
              <button type="button" className="btn btn-ghost"
                onClick={() => { setMode('idle'); setCode(''); setError(''); }}>
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
